/* Admin Walk-in Enrolment — one screen, one click:
   - capture parent + student
   - pick course → teacher → date → slot (uses public booking slots endpoint)
   - choose payment method (link emailed to parent, or counter cash/card/transfer)
   - generates invoice + schedule + studio booking + notifications all in one shot */
import { useEffect, useMemo, useState } from "react";
import api, { useList, errMsg, fmtRM, dayName, DAYS } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  UserPlus, Music4, GraduationCap, CalendarDays, Clock, CreditCard, Mail,
  Banknote, CheckCircle2, ArrowRight, ArrowLeft,
} from "lucide-react";

const todayPlus = (days) => {
  const d = new Date(); d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function WalkInEnrolPage() {
  const [step, setStep] = useState(0); // 0 parent+student, 1 course+slot, 2 payment, 3 success
  const [parent, setParent] = useState({ name: "", phone: "", email: "" });
  const [student, setStudent] = useState({ name: "", age: "" });
  const [notes, setNotes] = useState("");
  const [courseId, setCourseId] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [date, setDate] = useState(todayPlus(1));
  const [slots, setSlots] = useState(null);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("link");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Deep-link from Front Desk: prefill parent + student when ?student_id= is passed.
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("student_id");
    if (!sid) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/front-desk/search?q=`);  // empty — but we just need the student
        const s = (data.results || []).find((r) => r.student_id === sid);
        // Empty search returns []; fallback to direct fetch
        if (!s) {
          const r = await api.get(`/students/${sid}`);
          const st = r.data;
          setStudent({ name: st?.full_name || "", age: st?.age || "" });
          // best-effort parent prefill
          if (st?.parent_name) setParent({ name: st.parent_name, phone: st.parent_phone || "", email: st.parent_email || "" });
        } else {
          setStudent({ name: s.student_name, age: "" });
          const p = s.parents?.[0];
          if (p) setParent({ name: p.full_name || "", phone: p.phone || "", email: p.email || "" });
        }
        toast.info(`Prefilled for existing student — pick a course to add another class`);
      } catch { /* silent */ }
    })();
  }, []);

  const { data: courses } = useList("/courses");
  const { data: courseTeachers } = useList(
    courseId ? `/public/course-teachers?course_id=${courseId}` : null);

  // fetch all-in price whenever course changes
  useEffect(() => {
    if (!courseId) { setQuote(null); return; }
    api.get(`/public/enrolments/quote?course_id=${courseId}`)
      .then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [courseId]);

  // fetch available slots whenever teacher+date change
  useEffect(() => {
    if (!teacherId || !date) { setSlots(null); return; }
    setSlots(null);
    setPickedSlot(null);
    api.get(`/public/booking/slots?teacher_id=${teacherId}&date=${date}`)
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]));
  }, [teacherId, date]);

  const activeCourses = useMemo(
    () => (courses || []).filter((c) => c.status !== "inactive"), [courses]);
  const teachersForCourse = courseTeachers?.teachers || [];

  const submit = async () => {
    if (!parent.name || !parent.phone) return toast.error("Parent name and phone are required");
    if (!student.name) return toast.error("Student name is required");
    if (!pickedSlot) return toast.error("Pick a time slot");
    setBusy(true);
    try {
      const payload = {
        parent_name: parent.name, parent_phone: parent.phone,
        parent_email: parent.email || null,
        student_name: student.name, student_age: student.age || null,
        course_id: courseId, teacher_id: teacherId, studio_id: pickedSlot.studio_id,
        selected_date: date, selected_start_time: pickedSlot.start_time,
        selected_end_time: pickedSlot.end_time, payment_method: paymentMethod,
        notes: notes || null,
      };
      if (paymentMethod === "link") payload.origin_url = window.location.origin;
      const r = await api.post("/admin/walk-in-enrolments", payload);
      setResult({ ...r.data, paymentMethod });
      setStep(3);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  // -------- SUCCESS SCREEN --------
  if (step === 3 && result) {
    const isLink = result.paymentMethod === "link";
    return (
      <div className="max-w-2xl" data-testid="walk-in-success">
        <PageHeader title={isLink ? "Payment link generated" : "Walk-in enrolment confirmed"}
          description="The slot is reserved, parent and teacher have been notified." />
        <div className="rounded-lg border bg-secondary/5 p-6 mt-2 space-y-3">
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 className="h-5 w-5" />
            <p className="font-semibold">{result.message}</p>
          </div>
          {result.invoice_number && (
            <div className="text-sm space-y-1">
              <p><span className="text-muted-foreground">Invoice:</span> <b className="font-mono">{result.invoice_number}</b></p>
              <p><span className="text-muted-foreground">Amount paid:</span> <b>{fmtRM(result.total_paid)}</b> ({result.payment_method?.replace("_", " ")})</p>
            </div>
          )}
          {isLink && (
            <>
              <p className="text-sm">Total: <b>{fmtRM(result.total_payable)}</b> · {result.email_sent ? "✉ Email sent to parent." : "Share this link with the parent:"}</p>
              <Input readOnly value={result.checkout_url} className="font-mono text-xs"
                     onClick={(e) => e.target.select()} data-testid="walk-in-payment-link" />
              <Button size="sm" variant="outline"
                      onClick={() => { navigator.clipboard.writeText(result.checkout_url); toast.success("Link copied"); }}>
                Copy link
              </Button>
            </>
          )}
        </div>
        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={() => {
            setStep(0); setParent({ name: "", phone: "", email: "" });
            setStudent({ name: "", age: "" }); setNotes(""); setCourseId(""); setTeacherId("");
            setPickedSlot(null); setResult(null); setSlots(null);
          }} data-testid="walk-in-new-button">
            <UserPlus className="h-4 w-4 mr-1" />Enrol another walk-in
          </Button>
          <Button asChild data-testid="walk-in-view-enrolments">
            <a href="/admin/growth/enrolments">View all enrolments →</a>
          </Button>
        </div>
      </div>
    );
  }

  // -------- WIZARD --------
  const stepValid =
    step === 0 ? parent.name && parent.phone && student.name :
    step === 1 ? courseId && teacherId && pickedSlot :
    step === 2 ? !!paymentMethod : true;

  return (
    <div className="max-w-3xl" data-testid="walk-in-enrol-page">
      <PageHeader title="Walk-in enrolment"
        description="Front-desk shortcut: capture parent details, pick a class, take payment in one go." />

      {/* progress */}
      <div className="flex items-center gap-2 mb-8">
        {["Parent & student", "Class & slot", "Payment"].map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold
              ${i < step ? "bg-emerald-600 text-white" : i === step ? "bg-secondary text-white" : "bg-muted text-muted-foreground"}`}>
              {i < step ? "✓" : i + 1}
            </span>
            <span className={`text-xs font-medium hidden sm:block ${i === step ? "text-foreground" : "text-muted-foreground"}`}>{label}</span>
            {i < 2 && <span className="w-5 h-px bg-border hidden sm:block" />}
          </div>
        ))}
      </div>

      {/* STEP 0: parent + student */}
      {step === 0 && (
        <div className="rounded-lg border bg-card p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Parent full name *</Label>
            <Input value={parent.name} onChange={(e) => setParent({ ...parent, name: e.target.value })}
                   data-testid="walk-in-parent-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Parent phone *</Label>
            <Input value={parent.phone} onChange={(e) => setParent({ ...parent, phone: e.target.value })}
                   data-testid="walk-in-parent-phone" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Parent email <span className="text-xs text-muted-foreground">(receipt & secure account claim)</span></Label>
            <Input type="email" value={parent.email}
                   onChange={(e) => setParent({ ...parent, email: e.target.value })}
                   data-testid="walk-in-parent-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Student full name *</Label>
            <Input value={student.name} onChange={(e) => setStudent({ ...student, name: e.target.value })}
                   data-testid="walk-in-student-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Student age</Label>
            <Input value={student.age} onChange={(e) => setStudent({ ...student, age: e.target.value })}
                   data-testid="walk-in-student-age" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      data-testid="walk-in-notes" />
          </div>
        </div>
      )}

      {/* STEP 1: course + teacher + slot */}
      {step === 1 && (
        <div className="space-y-5">
          <div className="rounded-lg border bg-card p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1"><Music4 className="h-3 w-3" />Course *</Label>
              <Select value={courseId} onValueChange={(v) => { setCourseId(v); setTeacherId(""); setPickedSlot(null); }}>
                <SelectTrigger data-testid="walk-in-course-select"><SelectValue placeholder="Pick a course" /></SelectTrigger>
                <SelectContent>
                  {activeCourses.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1"><GraduationCap className="h-3 w-3" />Teacher *</Label>
              <Select value={teacherId} onValueChange={(v) => { setTeacherId(v); setPickedSlot(null); }} disabled={!courseId}>
                <SelectTrigger data-testid="walk-in-teacher-select">
                  <SelectValue placeholder={courseId ? "Pick a teacher" : "Pick a course first"} />
                </SelectTrigger>
                <SelectContent>
                  {teachersForCourse.length === 0
                    ? <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                        No teachers tagged for this course yet. Tag them in Admin → Teachers.
                      </div>
                    : teachersForCourse.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.full_name} {t.matched_by === "course_ids" && " · ✓"}
                        </SelectItem>
                      ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />First lesson date *</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                     min={todayPlus(0)} data-testid="walk-in-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Day</Label>
              <p className="text-sm pt-2 text-muted-foreground">
                {date ? dayName(new Date(date + "T00:00:00").getDay() === 0 ? 6 : new Date(date + "T00:00:00").getDay() - 1) : "—"}
              </p>
            </div>
          </div>

          {teacherId && (
            <div className="rounded-lg border bg-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-4 w-4 text-secondary" />
                <h3 className="font-semibold">Available slots on {date}</h3>
              </div>
              {slots === null ? <Spinner />
                : slots.length === 0
                  ? <p className="text-sm text-muted-foreground py-4">No open slots that day — try another date or teacher.</p>
                  : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                      {slots.map((s, i) => (
                        <button key={i}
                          onClick={() => setPickedSlot(s)}
                          className={`text-left rounded-md border px-3 py-2 text-sm transition
                            ${pickedSlot === s ? "bg-secondary text-white border-secondary" : "bg-card hover:border-secondary"}`}
                          data-testid={`walk-in-slot-${i}`}>
                          <p className="font-semibold">{s.start_time}–{s.end_time}</p>
                          <p className="text-xs opacity-80">{s.studio_name}</p>
                        </button>
                      ))}
                    </div>
                  )}
            </div>
          )}

          {quote && (
            <div className="rounded-lg border bg-secondary/5 p-4 text-sm space-y-1.5">
              <p className="font-semibold mb-2">Quote (full breakdown)</p>
              {quote.lines.map((ln, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className={ln.kind === "liability" ? "text-amber-700" : "text-muted-foreground"}>{ln.label}</span>
                  <b className={ln.kind === "liability" ? "text-amber-700" : ""}>{fmtRM(ln.amount)}</b>
                </div>
              ))}
              <div className="flex justify-between pt-2 border-t mt-1"><b>Total payable</b><b className="font-display text-secondary">{fmtRM(quote.total_payable)}</b></div>
              <p className="text-[10px] text-muted-foreground italic pt-1">Deposit is a non-refundable liability (applied to final month on drop-out), not revenue.</p>
            </div>
          )}
        </div>
      )}

      {/* STEP 2: payment method */}
      {step === 2 && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">How will the parent pay <b>{quote ? fmtRM(quote.total_payable) : ""}</b>?</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { v: "link", icon: Mail, label: "Email payment link", note: "Stripe checkout sent to parent's email; receipt + tax invoice arrive on payment." },
              { v: "cash", icon: Banknote, label: "Cash at counter", note: "Counter collected. Invoice marked paid immediately." },
              { v: "card", icon: CreditCard, label: "Card at counter", note: "Card terminal collected. Invoice marked paid immediately." },
              { v: "bank_transfer", icon: Banknote, label: "Bank transfer", note: "Parent has shown proof of transfer. Invoice marked paid immediately." },
            ].map((opt) => (
              <button key={opt.v}
                onClick={() => setPaymentMethod(opt.v)}
                className={`text-left rounded-lg border p-4 transition flex gap-3
                  ${paymentMethod === opt.v ? "border-secondary bg-secondary/5" : "bg-card hover:border-secondary/50"}`}
                data-testid={`walk-in-method-${opt.v}`}>
                <opt.icon className="h-5 w-5 mt-0.5 text-secondary shrink-0" />
                <div>
                  <p className="font-semibold text-sm">{opt.label}</p>
                  <p className="text-xs text-muted-foreground">{opt.note}</p>
                </div>
              </button>
            ))}
          </div>
          {paymentMethod === "link" && !parent.email && (
            <p className="text-xs text-amber-700">⚠️ No parent email — you'll have to copy the link manually after generating.</p>
          )}
        </div>
      )}

      {/* nav */}
      <div className="flex items-center gap-3 mt-8">
        {step > 0 && (
          <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="walk-in-back">
            <ArrowLeft className="h-4 w-4 mr-1" />Back
          </Button>
        )}
        {step < 2 && (
          <Button onClick={() => setStep(step + 1)} disabled={!stepValid} data-testid="walk-in-next">
            Continue<ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        )}
        {step === 2 && (
          <Button onClick={submit} disabled={busy} className="bg-secondary hover:bg-[#D96C52]"
                  data-testid="walk-in-submit">
            {busy ? "Processing…" : paymentMethod === "link" ? "Generate payment link" : "Confirm & record payment"}
          </Button>
        )}
      </div>
    </div>
  );
}
